/**
 * TemplateOverviewTab — what the template is for, how much it is used and where it can run. Demo page: /profile
 * (stat cards over info cards) and /ui-elements/cards.
 */
import React from 'react';
import { Badge, Card, StatStrip } from '../../../components/ui/index.js';
import type { AgentTemplate } from '../../../generated/api/types.gen.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { StatusBadge, formatPercent } from './detail-parts.js';

export const TemplateOverviewTab: React.FC<{ template: AgentTemplate }> = ({ template }) => {
  const usage = template.usageImpact;
  const applicability = template.applicability ?? [];
  return (
    <>
      <StatStrip
        className="mb-5"
        cells={[
          { label: 'Workflows', value: usage?.workflowCount ?? 0, sub: `${usage?.activeWorkflowCount ?? 0} active` },
          { label: 'Tasks', value: usage?.taskCount ?? 0 },
          { label: 'Employees', value: usage?.workforceAgentCount ?? 0 },
          { label: 'Daemons', value: usage?.daemonCount ?? 0, sub: `${usage?.activeDaemonCount ?? 0} active` },
        ]}
      />
      <Card title="Capability">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={template.status === 'active' ? 'success' : 'neutral'} dot>
            {sentenceCase(template.status ?? 'unknown')}
          </Badge>
          <Badge variant="info">Version {template.version ?? 1}</Badge>
          {template.source && <Badge variant="neutral">{sentenceCase(template.source)}</Badge>}
          <span className="ml-auto text-sm text-muted">
            Quality <span className="font-medium text-heading">{formatPercent(template.avgQualityScore)}</span>
          </span>
        </div>
        <p className="mt-3 max-w-4xl text-sm text-body">
          {template.goal || template.description || 'No goal is documented for this template yet.'}
        </p>
      </Card>
      <Card title="Where it can run">
        {applicability.length === 0 ? (
          <p className="text-sm text-muted">No applicability rules are recorded for this template.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {applicability.map((item) => (
              <div key={item.source} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-heading">{sentenceCase(item.source)}</span>
                  <StatusBadge status={item.status} />
                </div>
                <p className="mt-2 text-xs text-muted">{item.reason}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
};
